import React, { useState, useEffect } from 'react';
import { ModelCategory } from '../types';
import { ModelCategoryService } from '../services/api';
import AdminRoute from '../components/AdminRoute';
import {
  PartsContainer,
  PartsTitle,
  SectionTitle,
  PartsTable,
  TableHeader,
  TableCell,
  PartDescription,
  BooleanIndicator,
  ActionButton,
  HeaderContainer,
  LoadingContainer,
  ErrorContainer
} from './PartsManagement.styles';

const ModelCategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<ModelCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ModelCategory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true
  });

  // Load categories from API
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedCategories = await ModelCategoryService.getAllModelCategories();
      setCategories(fetchedCategories);
    } catch (error) {
      console.error('Error loading model categories:', error);
      setError('Failed to load model categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      isActive: true
    });
    setShowForm(true);
  };

  const handleEditCategory = (category: ModelCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      isActive: category.isActive
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      isActive: true
    });
    setEditingCategory(null);
    setShowForm(false);
  };

  const handleSaveCategory = async () => {
    try {
      setError(null);
      
      if (editingCategory) {
        // Update existing category
        const updatedCategory = await ModelCategoryService.updateModelCategory(editingCategory.id, {
          name: formData.name,
          description: formData.description,
          isActive: formData.isActive
        });

        setCategories(categories.map(c => c.id === editingCategory.id ? updatedCategory : c));
      } else {
        // Create new category
        const newCategory = await ModelCategoryService.createModelCategory({
          name: formData.name,
          description: formData.description,
          isActive: formData.isActive
        });

        setCategories([...categories, newCategory]);
      }

      setShowForm(false);
    } catch (error) {
      console.error('Error saving model category:', error);
      setError('Failed to save model category. Please try again.');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (window.confirm('Are you sure you want to delete this model category? This action cannot be undone.')) {
      try {
        setError(null);
        await ModelCategoryService.deleteModelCategory(categoryId);
        setCategories(categories.filter(c => c.id !== categoryId));
      } catch (error: any) {
        console.error('Error deleting model category:', error);
        if (error.response?.status === 400) {
          setError('Cannot delete this model category because it is currently in use by one or more models.');
        } else {
          setError('Failed to delete model category. Please try again.');
        }
      }
    }
  };

  if (loading) {
    return (
      <AdminRoute>
        <PartsContainer>
          <PartsTitle>Model Category Management</PartsTitle>
          <LoadingContainer>Loading model categories...</LoadingContainer>
        </PartsContainer>
      </AdminRoute>
    );
  }

  return (
    <AdminRoute>
      <PartsContainer>
        <HeaderContainer>
          <PartsTitle>Model Category Management</PartsTitle>
          <ActionButton onClick={handleCreateCategory}>
            Add New Category
          </ActionButton>
        </HeaderContainer>
        
        {error && (
          <ErrorContainer data-testid="error-message">
            {error}
            <ActionButton onClick={() => setError(null)}>
              Dismiss
            </ActionButton>
          </ErrorContainer>
        )}

        <div className="form-section">
          <label htmlFor="search">Search Categories</label>
          <input
            type="text"
            id="search"
            className="form-control"
            placeholder="Search by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {showForm && (
          <div className="form-section">
            <SectionTitle>
              {editingCategory ? 'Edit Category' : 'Create New Category'}
            </SectionTitle>
            
            <form onSubmit={(e) => { e.preventDefault(); handleSaveCategory(); }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label htmlFor="name">Category Name *</label>
                  <input
                    type="text"
                    id="name"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    />
                    {' '}Active
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  className="form-control"
                  placeholder="Optional description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn btn-primary">
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="form-section">
          <SectionTitle>Categories List ({filteredCategories.length} categories)</SectionTitle>
          <PartsTable>
            <thead>
              <tr>
                <TableHeader>Name</TableHeader>
                <TableHeader>Description</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Created</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.map(category => (
                <tr key={category.id}>
                  <TableCell>
                    <strong>{category.name}</strong>
                  </TableCell>
                  <TableCell>
                    {category.description && (
                      <PartDescription>
                        {category.description}
                      </PartDescription>
                    )}
                  </TableCell>
                  <TableCell>
                    <BooleanIndicator $value={category.isActive}>
                      {category.isActive ? 'Active' : 'Inactive'}
                    </BooleanIndicator>
                  </TableCell>
                  <TableCell>
                    {new Date(category.createdDate).toLocaleDateString()}
                    <PartDescription>
                      by {category.createdBy}
                    </PartDescription>
                  </TableCell>
                  <TableCell>
                    <ActionButton
                      onClick={() => handleEditCategory(category)}
                    >
                      Edit
                    </ActionButton>
                    <ActionButton
                      $variant="danger"
                      onClick={() => handleDeleteCategory(category.id)}
                    >
                      Delete
                    </ActionButton>
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </PartsTable>

          {filteredCategories.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              {searchTerm ? 'No categories found matching your search.' : 'No categories available. Create your first category.'}
            </div>
          )}
        </div>
      </PartsContainer>
    </AdminRoute>
  );
};

export default ModelCategoryManagement;